import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Button,
  Form,
  Alert,
  Modal,
  Image,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api";

const ADMIN_EMAILS = [
  "prateekmishrachs@gmail.com",
  "prateekmishrachs@gmail..com",
];

export default function AdminAds() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [destinationUrl, setDestinationUrl] = useState("");
  const [headline, setHeadline] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const isAdmin = useMemo(() => {
    const email = user?.email ? user.email.toLowerCase().trim() : "";
    return ADMIN_EMAILS.includes(email);
  }, [user?.email]);

  useEffect(() => {
    let ignore = false;

    const loadAd = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await api.get("/ads");
        if (ignore) {
          return;
        }
        const ad = res.data?.ad;
        setHeadline(ad?.headline || "");
        setDestinationUrl(ad?.destinationUrl || "");
        setBannerUrl(ad?.bannerUrl || "");
        setPreviewHtml(ad?.embedHtml || "");
      } catch (error) {
        console.error("Failed to load advertisement:", error);
        if (!ignore) {
          setLoadError(
            error.response?.data?.message ||
              "Unable to load current advertisement configuration."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadAd();
    return () => {
      ignore = true;
    };
  }, []);

  const handleSave = (event) => {
    event.preventDefault();
    setSaveError("");
    setSaveSuccess("");

    const trimmedHeadline = headline.trim();
    const trimmedDestination = destinationUrl.trim();
    const trimmedBanner = bannerUrl.trim();
    const embed = previewHtml.trim();

    if (!trimmedBanner && !embed) {
      setSaveError("Provide a banner image URL or embed HTML to advertise.");
      return;
    }

    if (
      trimmedDestination &&
      !/^https?:\/\//i.test(trimmedDestination) &&
      !trimmedDestination.startsWith("/")
    ) {
      setSaveError(
        "Destination URL must start with http(s):// or be a relative path beginning with /."
      );
      return;
    }

    const config = {
      headline: trimmedHeadline,
      destinationUrl: trimmedDestination,
      bannerUrl: trimmedBanner,
      embedHtml: embed,
    };

    const save = async () => {
      try {
        setSaving(true);
        const res = await api.put("/ads", {
          email: user?.email,
          headline: config.headline,
          destinationUrl: config.destinationUrl,
          bannerUrl: config.bannerUrl,
          embedHtml: config.embedHtml,
        });
        setSaveSuccess("Advertisement saved successfully.");
        const savedAd = res.data?.ad;
        if (savedAd) {
          setHeadline(savedAd.headline || "");
          setDestinationUrl(savedAd.destinationUrl || "");
          setBannerUrl(savedAd.bannerUrl || "");
          setPreviewHtml(savedAd.embedHtml || "");
        }
      } catch (error) {
        console.error("Failed to save advertisement:", error);
        setSaveError(
          error.response?.data?.message ||
            "Unable to save the advertisement configuration. Please try again."
        );
      } finally {
        setSaving(false);
      }
    };

    save();
  };

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Card
          className="shadow border-0 rounded-4 mx-auto"
          style={{ maxWidth: 620 }}
        >
          <Card.Body className="p-4 text-center">
            <h3 className="fw-bold text-danger mb-3">Access restricted</h3>
            <p className="text-muted mb-4">
              Only the administrator can manage advertisements. Please contact
              support if you believe this is an error.
            </p>
            <Button variant="primary" onClick={() => navigate("/")}>
              Return to home
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card
        className="shadow border-0 rounded-4 mx-auto"
        style={{ maxWidth: 720 }}
      >
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <h2 className="fw-bold text-primary mb-0">
              Manage Home Page Advertisement
            </h2>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => navigate("/admin/workers")}
            >
              Verify Workers
            </Button>
          </div>
          <p className="text-muted">
            Embed code, upload assets, or update call-to-action text for the
            home page ad slot. Changes you make here should point to the live
            promotion URL instead of the default placeholder. Paste your ad HTML
            or image URL below and update the destination link.
          </p>
          <Alert variant="info" className="mb-4">
            Tip: host static banner images inside <code>public/ads</code> and
            point to them with
            <code>/ads/your-banner.jpg</code>. Use the form below to keep track
            of the current promotion details.
          </Alert>
          {loadError && (
            <Alert variant="danger" className="mb-3">
              {loadError}
            </Alert>
          )}
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-3">
              <Form.Label>Promotion headline</Form.Label>
              <Form.Control
                value={headline}
                onChange={(event) => setHeadline(event.target.value)}
                placeholder="e.g. Summer Savings on Rentals"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Destination URL</Form.Label>
              <Form.Control
                type="url"
                value={destinationUrl}
                onChange={(event) => setDestinationUrl(event.target.value)}
                placeholder="https://your-live-campaign-url.example"
              />
              <Form.Text className="text-muted">
                Use a live campaign link here. This replaces the example.com
                placeholder.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Banner image URL</Form.Label>
              <Form.Control
                type="url"
                value={bannerUrl}
                onChange={(event) => setBannerUrl(event.target.value)}
                placeholder="/ads/my-banner.jpg or https://cdn.example/banner.png"
              />
              <Form.Text className="text-muted">
                Point to an asset inside <code>public/ads</code> or a hosted
                image.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Embed HTML or markdown</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={previewHtml}
                onChange={(event) => setPreviewHtml(event.target.value)}
                placeholder="Paste ad embed code here (optional)"
              />
              <Form.Text className="text-muted">
                Leave empty to run a simple banner. Paste HTML/JS snippets from
                ad networks here.
              </Form.Text>
            </Form.Group>
            {saveError && (
              <Alert variant="danger" className="mb-3">
                {saveError}
              </Alert>
            )}
            {saveSuccess && (
              <Alert variant="success" className="mb-3">
                {saveSuccess}
              </Alert>
            )}
            <div className="d-flex flex-wrap justify-content-end gap-2">
              <Button
                variant="outline-primary"
                onClick={() => setShowPreview(true)}
                disabled={loading || (!bannerUrl && !previewHtml)}
              >
                Preview ad
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving..." : "Save configuration"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Advertisement preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : previewHtml ? (
            <div
              className="border rounded p-3"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : bannerUrl ? (
            <div className="text-center">
              <a
                href={destinationUrl || "#"}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Image src={bannerUrl} alt="Ad preview" fluid rounded />
              </a>
            </div>
          ) : (
            <Alert variant="warning" className="mb-0">
              Provide a banner URL or embed code to preview the advertisement.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
